/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector, shallowEqual} from 'react-redux'
import {RootState} from '../../../setup'
import {useFormik} from 'formik'
import {ILeadsState} from './redux/LeadsRedux'
import {actions} from './redux/LeadsRedux'
import {actions as agentActions} from '../agents/redux/AgentsRedux'
import {LeadModel} from './models/LeadModel'
import {PageTitle} from '../../../_metronic/layout/core'
import LeadRow from './LeadRow'

const API_URL = process.env.REACT_APP_API_URL
const SHOW_MY_LEADS_KEY = 'show_only_my_leads'

// Custom CSS for notes editor
const notesStyles = `
  .notes-cell {
    max-width: 300px;
  }
  
  .notes-editor-wrapper {
    position: relative;
  }
  
  .notes-editor-wrapper.focused .notes-textarea {
    border-color: var(--bs-primary);
    box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
  }
  
  .notes-textarea {
    width: 100%;
    max-width: 100%;
    overflow-y: hidden;
    min-height: 30px !important;
    max-height: 60px !important;
  }
  
  .notes-textarea.empty {
    color: #a1a5b7;
  }
  
  .notes-textarea:focus {
    background-color: #ffffff;
  }
`

const LeadsPage: React.FC = () => {
  const dispatch = useDispatch()

  // Initialize with stored value from localStorage or default to false
  const [showOnlyMyLeads, setShowOnlyMyLeads] = useState(() => {
    const savedValue = localStorage.getItem(SHOW_MY_LEADS_KEY)
    return savedValue ? JSON.parse(savedValue) : false
  })

  const formik = useFormik({
    initialValues: {fromDate: '', toDate: ''},
    onSubmit: (values, {setStatus, setSubmitting, resetForm}) => {
      console.log(values)
      dispatch(actions.downloadCSV(values.fromDate, values.toDate))
      setSubmitting(false)
      setTimeout(() => {
        resetForm()
      }, 1000)
    },
  })

  const leads: ILeadsState = useSelector<RootState>(({leads}) => leads, shallowEqual) as ILeadsState
  const auth = useSelector<RootState>(({auth}) => auth, shallowEqual) as any

  // Get current user's ID for filtering
  const currentUserId = auth?.user?.id || ''

  useEffect(() => {
    // Load leads - if showOnlyMyLeads is true, filter by current user's ID
    const agentFilter = showOnlyMyLeads ? currentUserId : ''
    dispatch(actions.fetchPage(1, agentFilter))

    // Load agents for the dropdown
    dispatch(agentActions.fetchPage(1))

    // Load lead statuses
    dispatch(actions.fetchLeadStatuses())
  }, [dispatch, showOnlyMyLeads, currentUserId])

  const changePage = (page: number) => {
    const agentFilter = showOnlyMyLeads ? currentUserId : ''
    dispatch(actions.fetchPage(page, agentFilter))
  }

  const handleToggleMyLeads = () => {
    const newValue = !showOnlyMyLeads
    // Save to localStorage
    localStorage.setItem(SHOW_MY_LEADS_KEY, JSON.stringify(newValue))
    setShowOnlyMyLeads(newValue)
  }

  const getPageList = () => {
    let elements: JSX.Element[] = []
    leads.visiblePageList.forEach((page) => {
      elements.push(
        <li key={page} className={page === leads.currentPage ? 'page-item active' : 'page-item'}>
          <a href='#' className='page-link' onClick={() => changePage(page)}>
            {page}
          </a>
        </li>
      )
    })
    return elements
  }

  const getNextPageButton = () => {
    if (leads.visiblePageList[leads.visiblePageList.length - 1] < leads.totalPages) {
      return (
        <li className='page-item next'>
          <a href='#' onClick={() => changePage(++leads.currentPage)} className='page-link'>
            <i className='next'></i>
          </a>
        </li>
      )
    }
  }

  const getPreviousPageButton = () => {
    if (leads.visiblePageList[0] > 1) {
      return (
        <li className='page-item previous'>
          <a href='#' onClick={() => changePage(--leads.currentPage)} className='page-link'>
            <i className='previous'></i>
          </a>
        </li>
      )
    }
  }

  const getTableHeader = () => {
    let elements: JSX.Element[] = []
    if (leads.columns && leads.columns.length > 0) {
      leads.columns.forEach((column, index: number) => {
        let classes = 'min-w-150px'

        elements.push(
          <th key={index} className={classes}>
            {column.charAt(0).toUpperCase() + column.slice(1)}
          </th>
        )
      })
    }

    // Add the Created On header
    elements.push(
      <th key='created-on' className='min-w-125px'>
        <span className='d-flex align-items-center'>
          <i className='bi bi-calendar-date me-2'></i>
          Created On
        </span>
      </th>
    )

    elements.push(
      <th key='notes-column' className='min-w-150px notes-cell'>
        <span className='d-flex align-items-center'>
          <i className='bi bi-journal-text me-2'></i>
          Notes
        </span>
      </th>
    )

    // Add the Lead Status header
    elements.push(
      <th key='lead-status' className='min-w-150px'>
        <span className='d-flex align-items-center justify-content-center'>
          <i className='bi bi-tag me-2'></i>
          Lead Status
        </span>
      </th>
    )
    // Add the "Assigned To" header
    elements.push(
      <th key='assigned-to' className='min-w-150px text-end'>
        <span className='d-flex align-items-center justify-content-end'>
          <i className='bi bi-person me-2'></i>
          Assigned To
        </span>
      </th>
    )
    return elements
  }

  const getEmptyTable = () => {
    if (!leads.leads || leads.leads.length == 0) {
      return (
        <div className='row justify-content-md-center mt-20 mb-20'>
          <div className='col-md-auto'>Just getting started</div>
        </div>
      )
    }
  }

  const getRows = () => {
    let elements: JSX.Element[] = []
    if (leads.leads && leads.leads.length > 0) {
      leads.leads.forEach((lead: LeadModel, index: number) => {
        elements.push(
          <LeadRow
            key={index}
            lead={lead}
            columns={leads.columns}
            index={`${leads.currentPage}-${index}`}
          ></LeadRow>
        )
      })
      return elements
    }
  }

  return (
    <>
      <style>{notesStyles}</style>
      <PageTitle breadcrumbs={[]}>{'Leads'}</PageTitle>
      <div className={`card mb-5 mb-xl-8`}>
        {/* begin::Header */}
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            {/* <span className='card-label fw-bolder fs-3 mb-1'>Leads</span> */}
            {/* <span className='text-muted mt-1 fw-bold fs-7'>Over 500 orders</span> */}
          </h3>
          <div className='card-toolbar d-flex align-items-center'>
            {/* My Leads Toggle Switch */}
            <div className='form-check form-switch form-check-custom form-check-solid me-10'>
              <input
                className='form-check-input h-30px w-50px'
                type='checkbox'
                id='myLeadsToggle'
                checked={showOnlyMyLeads}
                onChange={handleToggleMyLeads}
              />
              <label className='form-check-label ms-2' htmlFor='myLeadsToggle'>
                <b>Only show my leads</b>
              </label>
            </div>

            <form className='form form-inline flex-row d-flex' onSubmit={formik.handleSubmit}>
              <input
                className='form-control me-3'
                type='date'
                max={formik.values.toDate}
                {...formik.getFieldProps('fromDate')}
              />
              <input
                className='form-control me-3'
                type='date'
                min={formik.values.fromDate}
                {...formik.getFieldProps('toDate')}
              />
              <button
                className='btn btn-sm btn-primary me-3 w-75'
                type='submit'
                disabled={formik.isSubmitting}
              >
                Download CSV
              </button>
            </form>
          </div>
        </div>
        {/* end::Header */}
        {/* begin::Body */}
        <div className='card-body py-3'>
          {getEmptyTable()}
          {/* begin::Table container */}
          <div className='table-responsive'>
            {/* begin::Table */}
            <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
              {/* begin::Table head */}
              <thead>
                <tr className='fw-bolder text-muted'>{getTableHeader()}</tr>
              </thead>
              {/* end::Table head */}
              {/* begin::Table body */}
              <tbody>{getRows()}</tbody>
              {/* end::Table body */}
            </table>
            {/* end::Table */}
          </div>
          {/* end::Table container */}
          <div className='d-flex flex-stack flex-wrap pt-10'>
            <div className='fs-6 fw-bold text-gray-700'>
              Showing {(leads.currentPage - 1) * leads.leadsPerPage + 1} to{' '}
              {Math.min(leads.currentPage * leads.leadsPerPage, leads.totalLeads)} of{' '}
              {leads.totalLeads} entries
            </div>

            <ul className='pagination'>
              {getPreviousPageButton()}
              {getPageList()}
              {getNextPageButton()}
            </ul>
          </div>
        </div>
        {/* begin::Body */}
      </div>
    </>
  )
}
export default LeadsPage
